import { useMemo, useState, Fragment } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RoleTemplatesAPI } from "@/lib/sprint3-api";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Search, Save, RotateCcw, Lock, Shield } from "lucide-react";
import { toast } from "sonner";

const ADMIN_ROLES = new Set(["super_admin", "inst_admin"]);

/**
 * Bird's-eye view of every permission against every role in one matrix.
 * Rows = permissions (grouped by resource). Columns = roles (system + custom).
 * Click cells to toggle, then "Save" persists changes per role.
 */
export default function PermissionMatrix() {
  const { user } = useAuth();
  const isSuper = (user?.roles || []).includes("super_admin");
  const canEdit = (user?.roles || []).some((r) => ADMIN_ROLES.has(r));
  const qc = useQueryClient();

  const tplQ = useQuery({ queryKey: ["role-templates"], queryFn: () => RoleTemplatesAPI.list() });
  const permQ = useQuery({ queryKey: ["permissions"], queryFn: () => RoleTemplatesAPI.permissions() });

  const [search, setSearch] = useState("");
  // overrides: { [roleKey]: Set<permissionKey> } — pending edits
  const [overrides, setOverrides] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const templates = useMemo(() => {
    const all = tplQ.data || [];
    return all.slice().sort((a, b) => (a.level ?? 0) - (b.level ?? 0) || a.name.localeCompare(b.name));
  }, [tplQ.data]);

  const permissions = useMemo(() => {
    const all = permQ.data?.permissions || [];
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter((p) => p.key.toLowerCase().includes(q) || p.label.toLowerCase().includes(q));
  }, [permQ.data, search]);

  const groups = useMemo(() => {
    const acc = {};
    permissions.forEach((p) => {
      const g = p.resource || p.key.split(":")[0] || "other";
      acc[g] = acc[g] || [];
      acc[g].push(p);
    });
    return acc;
  }, [permissions]);

  const isGranted = (roleKey, permKey) => {
    if (overrides[roleKey]) return overrides[roleKey].has(permKey);
    const tpl = templates.find((t) => t.key === roleKey);
    if (!tpl) return false;
    return tpl.permissions.includes(permKey) || tpl.permissions.includes("*");
  };

  const editableForUser = (tpl) => {
    if (!canEdit) return false;
    if (tpl.is_system) return isSuper;
    return true;
  };

  const toggle = (roleKey, permKey) => {
    const tpl = templates.find((t) => t.key === roleKey);
    if (!tpl || !editableForUser(tpl)) return;
    setOverrides((prev) => {
      const next = { ...prev };
      const cur = new Set(next[roleKey] || tpl.permissions);
      if (cur.has(permKey)) cur.delete(permKey);
      else cur.add(permKey);
      // Drop the "*" wildcard once user starts editing — we'll save explicit perms instead
      cur.delete("*");
      next[roleKey] = cur;
      return next;
    });
  };

  const resetRole = (roleKey) => {
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[roleKey];
      return next;
    });
  };

  const saveRole = async (tpl) => {
    if (!overrides[tpl.key]) return;
    setSavingKey(tpl.key);
    try {
      const perms = Array.from(overrides[tpl.key]).sort();
      await RoleTemplatesAPI.update(tpl.id, { permissions: perms });
      toast.success(`${tpl.name}: ${perms.length} permissions saved`);
      resetRole(tpl.key);
      qc.invalidateQueries({ queryKey: ["role-templates"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSavingKey(null);
    }
  };

  if (tplQ.isLoading || permQ.isLoading) {
    return <div className="p-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  }

  const hasPending = Object.keys(overrides).length > 0;

  return (
    <div data-testid="permission-matrix">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search permissions…"
            className="pl-8 h-8 text-xs"
            data-testid="pm-search"
          />
        </div>
        <div className="text-xs text-muted-foreground">
          {permissions.length} permissions × {templates.length} roles
          {hasPending && <Badge variant="outline" className="ml-2 text-[10px]">{Object.keys(overrides).length} unsaved</Badge>}
        </div>
      </div>

      <div className="border border-border rounded-md overflow-hidden bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-card z-10">
              <tr className="border-b">
                <th className="text-left p-2 min-w-[260px] sticky left-0 bg-card border-r z-20">Permission</th>
                {templates.map((t) => {
                  const dirty = !!overrides[t.key];
                  return (
                    <th key={t.key} className="px-2 py-2 text-center min-w-[80px] border-r last:border-r-0">
                      <div className="flex flex-col items-center gap-0.5">
                        <div className="flex items-center gap-1">
                          {t.is_system ? <Lock className="h-2.5 w-2.5 text-muted-foreground" /> : <Shield className="h-2.5 w-2.5 text-primary" />}
                          <span className="font-medium text-[10px] capitalize whitespace-nowrap">{t.name.split(" ").slice(0, 2).join(" ")}</span>
                        </div>
                        <code className="text-[8px] text-muted-foreground">{t.key}</code>
                        {dirty && (
                          <div className="flex gap-0.5 mt-0.5">
                            <Button size="icon" variant="ghost" className="h-4 w-4" onClick={() => saveRole(t)} disabled={savingKey === t.key} title="Save" data-testid={`pm-save-${t.key}`}>
                              {savingKey === t.key ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Save className="h-2.5 w-2.5 text-emerald-600" />}
                            </Button>
                            <Button size="icon" variant="ghost" className="h-4 w-4" onClick={() => resetRole(t.key)} title="Reset" data-testid={`pm-reset-${t.key}`}>
                              <RotateCcw className="h-2.5 w-2.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {Object.entries(groups).map(([group, perms]) => (
                <Fragment key={group}>
                  <tr className="bg-muted/40">
                    <td colSpan={templates.length + 1} className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                      {group}
                    </td>
                  </tr>
                  {perms.map((p) => (
                    <tr key={p.key} className="border-b last:border-b-0 hover:bg-muted/30" data-testid={`pm-row-${p.key}`}>
                      <td className="p-2 sticky left-0 bg-card border-r">
                        <div className="font-mono text-[10px] text-foreground">{p.key}</div>
                        <div className="text-[10px] text-muted-foreground truncate max-w-[240px]">{p.label}</div>
                      </td>
                      {templates.map((t) => {
                        const granted = isGranted(t.key, p.key);
                        const dirty = !!overrides[t.key];
                        const editable = editableForUser(t);
                        return (
                          <td
                            key={`${t.key}-${p.key}`}
                            className={`text-center border-r last:border-r-0 ${editable ? "cursor-pointer hover:bg-primary/5" : "cursor-not-allowed opacity-60"} ${dirty ? "bg-amber-50/40" : ""}`}
                            onClick={() => editable && toggle(t.key, p.key)}
                            data-testid={`pm-cell-${t.key}-${p.key}`}
                          >
                            <div className="flex items-center justify-center py-1.5">
                              <Checkbox
                                checked={granted}
                                disabled={!editable}
                                onCheckedChange={() => editable && toggle(t.key, p.key)}
                                className="h-3.5 w-3.5"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-3 text-[11px] text-muted-foreground flex items-center gap-3 flex-wrap">
        <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> System role · only super_admin can edit</span>
        <span className="flex items-center gap-1"><Shield className="h-3 w-3 text-primary" /> Custom role</span>
        <span>Amber background = unsaved edits. Click ✓ on column header to save.</span>
      </div>
    </div>
  );
}
